import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import { getLearningPath, getCachedWorldMap, saveWorldMap } from '../../services/firebase';
import WorldNode from './WorldNode';
import WorldPath from './WorldPath';
import WorldSelector from './WorldSelector';
import MapCharacter from './MapCharacter';
import WizardDialogue, {
    generateWelcomeDialogue,
    generateBeforeLessonDialogue,
    generateBeforeGameDialogue,
    generatePathMasteryDialogue,
    DIALOGUE_TYPES
} from './WizardDialogue';
import { getWorldTheme, generateFloatingPositions } from './WorldBackgrounds';
import './WorldMap.css';

function WorldMap() {
    const [worldMap, setWorldMap] = useState(null);
    const [currentWorld, setCurrentWorld] = useState(0);
    const [characterPosition, setCharacterPosition] = useState({ x: 120, y: 100 });
    const [isAnimating, setIsAnimating] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeAvatar, setActiveAvatar] = useState('student');

    // Dialogue state
    const [showDialogue, setShowDialogue] = useState(false);
    const [dialogueType, setDialogueType] = useState(DIALOGUE_TYPES.WELCOME);
    const [dialogueMessages, setDialogueMessages] = useState([]);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [hasSeenWelcome, setHasSeenWelcome] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userData, refreshUserData } = useAuth();

    // Get topic from state or localStorage (for refresh support)
    const stateTopic = location.state?.topic;
    const [topic, setTopic] = useState(() => {
        if (stateTopic) {
            // Save to localStorage for refresh support
            localStorage.setItem('currentWorldMapTopic', stateTopic);
            return stateTopic;
        }
        // Try to recover from localStorage
        return localStorage.getItem('currentWorldMapTopic') || null;
    });

    // Load world map data - include location.key to force reload when navigating back from games
    useEffect(() => {
        if (!topic) {
            navigate('/home');
            return;
        }
        // Update localStorage when topic changes
        if (stateTopic && stateTopic !== topic) {
            setTopic(stateTopic);
            localStorage.setItem('currentWorldMapTopic', stateTopic);
        }
        loadWorldMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic, currentUser, stateTopic, location.key]);

    // Pre-generate game content for unlocked game nodes (defined before loadWorldMap which uses it)
    const pregenerateGameContent = async (mapData, topicName) => {
        try {
            // Collect all unlocked game nodes that need content
            const gamesToPregenerate = [];

            mapData.worlds.forEach(world => {
                world.nodes.forEach(node => {
                    if (node.type === 'game' && node.unlocked && !node.completed) {
                        gamesToPregenerate.push({
                            templateId: node.gameType || 'trivia',
                            topic: topicName,
                            moduleTitle: node.title || world.title,
                            concepts: node.concepts || [],
                            difficulty: node.difficulty || 'intermediate'
                        });
                    }
                });
            });

            if (gamesToPregenerate.length > 0) {
                console.log(`🎮 Pre-generating ${gamesToPregenerate.length} games in background...`);

                // Fire and forget - don't await
                fetch(`${API_URL}/api/pregenerate-games`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ games: gamesToPregenerate.slice(0, 4) }) // Limit to next 4 games
                }).catch(() => console.log('Pre-generation request sent'));
            }
        } catch (error) {
            console.log('Pre-generation skipped:', error.message);
        }
    };

    const loadWorldMap = async () => {
        setLoading(true);
        try {
            if (!currentUser) {
                navigate('/auth');
                return;
            }

            // Refresh user data to get latest completions (especially important after returning from a game)
            await refreshUserData();

            // Get completed modules and games from user data (use fresh fetch since refreshUserData updates state async)
            const { getUserData } = await import('../../services/firebase');
            const freshUserData = await getUserData(currentUser.uid);
            const latestUserData = freshUserData.success ? freshUserData.data : userData;

            const completedModules = latestUserData?.completedModules || [];
            const completedGames = latestUserData?.completedGames || [];

            // Create a hash to detect if progress changed
            const progressHash = JSON.stringify([...completedModules.sort(), ...completedGames.map(g => g.key).sort()]);

            // Try to load cached world map first
            const cachedMap = await getCachedWorldMap(currentUser.uid, topic);

            if (cachedMap.success && cachedMap.data.completedModulesHash === progressHash) {
                console.log('📦 Loading cached world map from Firebase');
                const mapData = cachedMap.data;

                setWorldMap(mapData);

                // Set current world to first incomplete world
                const firstIncompleteWorld = mapData.worlds.findIndex(w => !w.completed);
                setCurrentWorld(firstIncompleteWorld >= 0 ? firstIncompleteWorld : 0);

                // Set character position to first unlocked incomplete node
                const activeWorld = mapData.worlds[firstIncompleteWorld >= 0 ? firstIncompleteWorld : 0];
                const firstUnlockedNode = activeWorld?.nodes.find(n => n.unlocked && !n.completed);
                if (firstUnlockedNode) {
                    setCharacterPosition(firstUnlockedNode.position);
                }

                // Pre-generate game content in background for faster loading
                pregenerateGameContent(mapData, topic);

                setLoading(false);
                return;
            }

            console.log('🔄 Generating fresh world map (progress changed or no cache)');

            // Get the learning path
            const cachedResult = await getLearningPath(currentUser.uid, topic);

            if (!cachedResult.success) {
                console.error('No learning path found for topic:', topic);
                navigate('/home');
                return;
            }

            const learningPath = cachedResult.data;

            // Generate world map from learning path
            const response = await fetch(`${API_URL}/api/generate-world-map`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    learningPath,
                    userId: currentUser.uid,
                    completedModules,
                    completedGames
                })
            });

            const data = await response.json();

            if (data.success) {
                // Cache the world map for future use
                await saveWorldMap(currentUser.uid, topic, data.worldMap, progressHash);

                setWorldMap(data.worldMap);

                // Set current world to first incomplete world
                const firstIncompleteWorld = data.worldMap.worlds.findIndex(w => !w.completed);
                setCurrentWorld(firstIncompleteWorld >= 0 ? firstIncompleteWorld : 0);

                // Set character position to first unlocked incomplete node
                const activeWorld = data.worldMap.worlds[firstIncompleteWorld >= 0 ? firstIncompleteWorld : 0];
                const firstUnlockedNode = activeWorld?.nodes.find(n => n.unlocked && !n.completed);
                if (firstUnlockedNode) {
                    setCharacterPosition(firstUnlockedNode.position);
                }

                // Pre-generate game content in background for faster loading
                pregenerateGameContent(data.worldMap, topic);
            }
        } catch (error) {
            console.error('Error loading world map:', error);
        }
        setLoading(false);
    };

    // Show welcome dialogue on first visit
    useEffect(() => {
        if (!loading && worldMap && !hasSeenWelcome && currentUser) {
            // Check localStorage to see if user has seen welcome for this topic
            const welcomeKey = `wizard_welcome_${currentUser.uid}_${topic}`;
            const hasSeenBefore = localStorage.getItem(welcomeKey);

            if (!hasSeenBefore) {
                const playerName = userData?.displayName || userData?.email?.split('@')[0] || 'Apprentice';
                setDialogueMessages(generateWelcomeDialogue(playerName, topic));
                setDialogueType(DIALOGUE_TYPES.WELCOME);
                setShowDialogue(true);
                localStorage.setItem(welcomeKey, 'true');
            }
            setHasSeenWelcome(true);
        }
    }, [loading, worldMap, hasSeenWelcome, currentUser, topic, userData]);

    // Check for path mastery (all worlds complete)
    useEffect(() => {
        if (!worldMap || !currentUser) return;

        const allWorldsComplete = worldMap.worlds.every(w => w.completed);
        if (allWorldsComplete && worldMap.worlds.length > 0) {
            // Check if player has already seen mastery dialogue
            const masteryKey = `wizard_mastery_${currentUser.uid}_${topic}`;
            const hasSeenMastery = localStorage.getItem(masteryKey);

            if (!hasSeenMastery) {
                // Show mastery celebration dialogue
                const playerName = userData?.displayName || userData?.email?.split('@')[0] || 'Apprentice';
                setDialogueMessages(generatePathMasteryDialogue(playerName, topic));
                setDialogueType(DIALOGUE_TYPES.PATH_MASTERY);
                setShowDialogue(true);
                localStorage.setItem(masteryKey, 'true');
            }

            // Player has mastered the path - become a wizard!
            setActiveAvatar('wizard');
        }
    }, [worldMap, currentUser, topic, userData]);

    // Handle dialogue completion
    const handleDialogueComplete = useCallback(() => {
        setShowDialogue(false);

        // If there's a pending navigation, execute it
        if (pendingNavigation) {
            navigate(pendingNavigation.path, { state: pendingNavigation.state });
            setPendingNavigation(null);
        }
    }, [navigate, pendingNavigation]);

    // Show dialogue before navigating
    const showPreNavigationDialogue = useCallback((node, navigationPath, navigationState) => {
        let messages = [];
        let type = DIALOGUE_TYPES.BEFORE_LESSON;

        if (node.type === 'lesson') {
            messages = generateBeforeLessonDialogue(node.title);
            type = DIALOGUE_TYPES.BEFORE_LESSON;
        } else if (node.type === 'mini_game') {
            messages = generateBeforeGameDialogue(node.title || 'challenge');
            type = DIALOGUE_TYPES.BEFORE_GAME;
        }

        if (messages.length > 0) {
            setDialogueMessages(messages);
            setDialogueType(type);
            setPendingNavigation({ path: navigationPath, state: navigationState });
            setShowDialogue(true);
            return true;
        }
        return false;
    }, []);

    // Handle node click
    const handleNodeClick = useCallback(async (node, skipDialogue = false) => {
        if (!node.unlocked || isAnimating || !worldMap) return;

        const currentActiveWorld = worldMap.worlds[currentWorld];
        setSelectedNode(node);

        // Animate character to node
        setIsAnimating(true);
        setCharacterPosition(node.position);

        // Wait for animation
        await new Promise(resolve => setTimeout(resolve, 600));
        setIsAnimating(false);

        // Determine navigation path and state
        let navigationPath = '';
        let navigationState = {};

        if (node.type === 'lesson') {
            navigationPath = '/interactive-lesson';
            navigationState = {
                topic: worldMap.topic,
                moduleId: node.moduleId,
                fromWorldMap: true
            };
        } else if (node.type === 'mini_game') {
            navigationPath = '/game';
            navigationState = {
                templateId: node.gameTemplateId,
                topic: worldMap.topic,
                moduleTitle: node.title,
                concepts: [],
                difficulty: currentActiveWorld?.difficulty || 'intermediate',
                worldNodeId: node.nodeId,
                learningPathId: worldMap.topic
            };
        } else if (node.type === 'boss') {
            navigationPath = '/boss-battle';
            navigationState = {
                topic: worldMap.topic,
                moduleId: node.bossModuleId,
                levelNumber: currentWorld + 1
            };
        }

        // Show dialogue before navigation (only for uncompleted nodes and if not skipping)
        if (!skipDialogue && !node.completed && (node.type === 'lesson' || node.type === 'mini_game')) {
            const dialogueShown = showPreNavigationDialogue(node, navigationPath, navigationState);
            if (dialogueShown) return;
        }

        // Navigate directly if no dialogue or dialogue skipped
        if (navigationPath) {
            navigate(navigationPath, { state: navigationState });
        }
    }, [isAnimating, navigate, worldMap, currentWorld, showPreNavigationDialogue]);

    // Handle world change
    const handleWorldChange = (worldIndex) => {
        if (worldMap.worlds[worldIndex]?.unlocked) {
            setCurrentWorld(worldIndex);

            // Move character to first node of new world
            const newWorld = worldMap.worlds[worldIndex];
            const firstNode = newWorld.nodes[0];
            if (firstNode) {
                setCharacterPosition(firstNode.position);
            }
        }
    };

    // Calculate total stars and progress
    const calculateProgress = () => {
        if (!worldMap) return { totalStars: 0, maxStars: 0, completedNodes: 0, totalNodes: 0 };

        let totalStars = 0;
        let maxStars = 0;
        let completedNodes = 0;
        let totalNodes = 0;

        worldMap.worlds.forEach(world => {
            world.nodes.forEach(node => {
                totalStars += node.stars || 0;
                maxStars += 3;
                totalNodes++;
                if (node.completed) completedNodes++;
            });
        });

        return { totalStars, maxStars, completedNodes, totalNodes };
    };

    // Map world index to theme - cycles through available themes
    const themeIds = ['island', 'forest', 'mountain', 'desert', 'space', 'ocean', 'volcano', 'crystal'];
    const currentThemeId = themeIds[currentWorld % themeIds.length];
    const currentTheme = getWorldTheme(currentThemeId);

    // Generate floating background elements (memoized to prevent re-generation on every render)
    const floatingElements = useMemo(() => generateFloatingPositions(currentTheme), [currentTheme]);

    // Avatar emoji map
    const avatarEmojis = useMemo(() => ({
        wizard: '🧙‍♂️',
        hero: '🦸',
        elf: '🧝',
        robot: '🤖',
        fox: '🦊',
        dragon: '🐉',
        rocket: '🚀',
        star: '🌟'
    }), []);

    if (loading) {
        return (
            <div className="world-map-loading">
                <div className="loading-content">
                    <div className="loading-character">🧙‍♂️</div>
                    <div className="loading-spinner"></div>
                    <p>Generating your adventure map...</p>
                </div>
            </div>
        );
    }

    if (!worldMap) {
        return (
            <div className="world-map-error">
                <h2>Couldn't load world map</h2>
                <button className="btn btn-primary" onClick={() => navigate('/home')}>
                    Go Home
                </button>
            </div>
        );
    }

    const activeWorld = worldMap.worlds[currentWorld];
    const progress = calculateProgress();

    return (
        <div className="world-map-container">
            {/* Header */}
            <div className="world-map-header">
                <button className="back-btn" onClick={() => navigate('/learning-path', { state: { topic } })}>
                    ← Back
                </button>
                <div className="header-center">
                    <h1>{worldMap.topic}</h1>
                    <p className="adventure-subtitle">
                        {activeAvatar === 'wizard' ? (
                            <span className="master-badge">🧙 Master Wizard - Path Complete!</span>
                        ) : (
                            'Your Learning Adventure'
                        )}
                    </p>
                </div>
                <div className="header-stats">
                    <div className="stat-item">
                        <span className="stat-icon">⭐</span>
                        <span className="stat-value">{progress.totalStars}/{progress.maxStars}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-icon">💎</span>
                        <span className="stat-value">{userData?.totalXP || 0}</span>
                    </div>
                </div>
            </div>

            {/* World Selector */}
            <WorldSelector
                worlds={worldMap.worlds}
                currentWorld={currentWorld}
                onSelectWorld={handleWorldChange}
            />

            {/* Map Area */}
            <div
                className={`world-map-area ${currentTheme.ambientClass}`}
                style={{
                    background: currentTheme.gradient
                }}
            >
                <div className="map-canvas">
                {/* Floating Background Elements */}
                <div className="floating-background-layer">
                    {floatingElements.map(element => (
                        <div
                            key={element.id}
                            className="floating-bg-element"
                            style={{
                                left: element.left,
                                top: element.top,
                                fontSize: `${element.size}rem`,
                                animationDelay: `${element.animationDelay}s`,
                                animationDuration: `${element.animationDuration}s`
                            }}
                        >
                            {element.emoji}
                        </div>
                    ))}
                </div>

                {/* Ambient Particles */}
                <div className="ambient-particles" style={{ '--particle-color': currentTheme.particleColor }}>
                    {[...Array(20)].map((_, i) => (
                        <div
                            key={`particle-${i}`}
                            className="ambient-particle"
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 10}s`,
                                animationDuration: `${8 + Math.random() * 7}s`
                            }}
                        />
                    ))}
                </div>

                {/* World Title */}
                <div className="world-title-banner">
                    <span className="world-emoji">{activeWorld.themeEmoji}</span>
                    <div className="world-title-text">
                        <span className="world-number">World {activeWorld.worldNumber}</span>
                        <h2>{activeWorld.name}</h2>
                    </div>
                    <span className="world-difficulty">{activeWorld.difficulty}</span>
                </div>

                {/* Decorations */}
                {activeWorld.decorations.map(deco => (
                    <div
                        key={deco.id}
                        className="decoration"
                        style={{
                            left: deco.position.x,
                            top: deco.position.y,
                            fontSize: `${deco.size * 2}rem`,
                            opacity: 0.6
                        }}
                    >
                        {deco.emoji}
                    </div>
                ))}

                {/* Collectibles */}
                {activeWorld.collectibles.map(collectible => (
                    !collectible.collected && (
                        <div
                            key={collectible.id}
                            className="collectible"
                            style={{
                                left: collectible.position.x,
                                top: collectible.position.y
                            }}
                        >
                            {collectible.emoji}
                        </div>
                    )
                ))}

                {/* Paths between nodes */}
                <svg className="paths-layer" width="100%" height="100%">
                    {activeWorld.nodes.map(node =>
                        node.connections.map(targetId => {
                            const targetNode = activeWorld.nodes.find(n => n.nodeId === targetId);
                            if (!targetNode) return null;
                            return (
                                <WorldPath
                                    key={`${node.nodeId}-${targetId}`}
                                    from={node.position}
                                    to={targetNode.position}
                                    completed={node.completed}
                                    unlocked={node.unlocked}
                                />
                            );
                        })
                    )}
                </svg>

                {/* Nodes */}
                {activeWorld.nodes.map(node => (
                    <WorldNode
                        key={node.nodeId}
                        node={node}
                        isSelected={selectedNode?.nodeId === node.nodeId}
                        onClick={() => handleNodeClick(node)}
                    />
                ))}

                {/* Animated Character */}
                <MapCharacter
                    position={characterPosition}
                    avatar={avatarEmojis[activeAvatar] || '🧙‍♂️'}
                    isMoving={isAnimating}
                />
                </div>{/* End map-canvas */}
            </div>

            {/* Progress Bar */}
            <div className="world-progress-bar">
                <div className="progress-info">
                    <span className="progress-label">
                        {activeWorld.themeEmoji} {activeWorld.name} Progress
                    </span>
                    <span className="progress-stats">
                        {activeWorld.nodes.filter(n => n.completed).length} / {activeWorld.nodes.length} Complete
                    </span>
                </div>
                <div className="progress-track">
                    <div
                        className="progress-fill"
                        style={{
                            width: `${(activeWorld.nodes.filter(n => n.completed).length / activeWorld.nodes.length) * 100}%`
                        }}
                    />
                </div>
            </div>

            {/* Node Details Panel (when selected) */}
            {selectedNode && (
                <div className="node-details-panel">
                    <div className="node-details-header">
                        <span className="node-icon">{selectedNode.icon}</span>
                        <div className="node-info">
                            <h3>{selectedNode.title}</h3>
                            <p>{selectedNode.description}</p>
                        </div>
                        <button className="close-btn" onClick={() => setSelectedNode(null)}>✕</button>
                    </div>
                    <div className="node-details-body">
                        <div className="node-reward">
                            <span>💎 {selectedNode.xpReward} XP</span>
                        </div>
                        <div className="node-stars">
                            {[1, 2, 3].map(star => (
                                <span
                                    key={star}
                                    className={`star ${star <= selectedNode.stars ? 'earned' : ''}`}
                                >
                                    ⭐
                                </span>
                            ))}
                        </div>
                    </div>
                    {selectedNode.unlocked && !selectedNode.completed && (
                        <button
                            className="btn btn-primary start-btn"
                            onClick={() => handleNodeClick(selectedNode)}
                        >
                            {selectedNode.type === 'lesson' ? 'Start Lesson' :
                             selectedNode.type === 'boss' ? 'Enter Boss Battle' :
                             'Play Game'} →
                        </button>
                    )}
                    {selectedNode.completed && (
                        <button
                            className="btn btn-secondary replay-btn"
                            onClick={() => handleNodeClick(selectedNode)}
                        >
                            Replay ↻
                        </button>
                    )}
                    {!selectedNode.unlocked && (
                        <div className="locked-message">
                            🔒 Complete previous nodes to unlock
                        </div>
                    )}
                </div>
            )}

            {/* Wizard Dialogue */}
            <WizardDialogue
                isOpen={showDialogue}
                onClose={() => setShowDialogue(false)}
                dialogueType={dialogueType}
                messages={dialogueMessages}
                playerName={userData?.displayName || userData?.email?.split('@')[0] || 'Apprentice'}
                playerAvatar={activeAvatar}
                wizardMood={
                    dialogueType === DIALOGUE_TYPES.PATH_MASTERY ? 'celebrating' :
                    dialogueType === DIALOGUE_TYPES.WELCOME ? 'teaching' :
                    dialogueType === DIALOGUE_TYPES.BEFORE_GAME ? 'excited' :
                    'neutral'
                }
                onComplete={handleDialogueComplete}
            />
        </div>
    );
}

export default WorldMap;
